import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
  RefObject
} from 'react'
import { useAppSelector } from '../../../app/hooks'
import {
  XCAISecurityContext,
  XCRecognitionResult,
  XCRecognitionStatus,
  XCSelectionInfo,
  XCSafetyScenario,
  XCFilterParams,
  XCBatchProgress
} from '../types/contextInterface'
import { XC_RECOGNITION_STATUS } from '../types/contextInterface'
import { XCImageSelectionEditorRef } from '../../components/ImageSelectionEditor'
import dayjs from 'dayjs'

// Context创建
const AISecurityContext = createContext<XCAISecurityContext | undefined>(
  undefined
)

// Provider组件Props
interface XCAISecurityProviderProps {
  children: ReactNode
}

// Provider组件
export const AISecurityProvider: React.FC<XCAISecurityProviderProps> = ({
  children
}) => {
  // 从Redux store获取用户和团队信息
  const userId = useAppSelector((state) =>
    state.main.user?.user_id
  )
  const groupId = useAppSelector((state) => state.group.groupId)
  const isVip = useAppSelector((state) => state.group.isVip)
  const myRole = useAppSelector((state) => state.group.myRole)

  const [recognitionStatus, setRecognitionStatus] = useState<
    XCRecognitionStatus
  >(XC_RECOGNITION_STATUS.WAITING)

  const [selectedImages, setSelectedImages] = useState<string[]>([])

  const [selectionInfo, setSelectionInfo] = useState<XCSelectionInfo[]>([])

  const [recognitionResult, setRecognitionResultState] = useState<
    XCRecognitionResult
  >()

  const [selectedSafetyScenarios, setSelectedSafetyScenarios] = useState<
    XCSafetyScenario[]
  >(() => {
    const stored = localStorage.getItem(
      'ai_recognition_selected_safety_scenarios'
    )
    return stored ? JSON.parse(stored) : []
  })
  const [safetyScenarioList, setSafetyScenarioList] = useState<
    XCSafetyScenario[]
  >(() => {
    const stored = localStorage.getItem('ai_recognition_safety_scenario_list')
    return stored ? JSON.parse(stored) : []
  })

  /** 记住本次选择 */
  const [rememberChoice, setRememberChoice] = useState(() => {
    const stored = localStorage.getItem('ai_recognition_remember_choice_v2')
    return stored ? JSON.parse(stored) : false
  })

  /** 安全场景选择Modal状态 */
  const [showSafetyScenarioModal, setShowSafetyScenarioModal] = useState(false)

  // 批量识别状态
  const [isBatchMode, setIsBatchModeState] = useState<boolean>(false)
  const [batchProgress, setBatchProgressState] = useState<XCBatchProgress>({
    total: 0,
    completed: 0,
    failed: 0,
    inFlight: 0
  })

  // ImageSelectionEditor 引用状态
  const [imageSelectionEditorRef, setImageSelectionEditorRefState] = useState<
    RefObject<XCImageSelectionEditorRef>
  >()

  // 设置 ImageSelectionEditor 引用的方法
  const setImageSelectionEditorRef = useCallback(
    (ref: RefObject<XCImageSelectionEditorRef>) => {
      setImageSelectionEditorRefState(ref)
    },
    []
  )

  // 包装 setRecognitionResult 为简单函数类型
  const setRecognitionResult = useCallback((result?: XCRecognitionResult) => {
    setRecognitionResultState(result)
  }, [])

  // 包装 setRememberChoice 为带有 localStorage 存储的函数
  const setRememberChoiceWithStorage = useCallback((value: boolean) => {
    setRememberChoice(value)
    localStorage.setItem(
      'ai_recognition_remember_choice_v2',
      JSON.stringify(value)
    )
  }, [])

  // 包装 setSelectedSafetyScenarios 为带有 localStorage 存储的函数
  const setSelectedSafetyScenariosWithStorage = useCallback(
    (value: XCSafetyScenario[]) => {
      setSelectedSafetyScenarios(value)
      localStorage.setItem(
        'ai_recognition_selected_safety_scenarios',
        JSON.stringify(value)
      )
    },
    []
  )

  // 包装 setSafetyScenarioList 为带有 localStorage 存储的函数
  const setSafetyScenarioListWithStorage = useCallback(
    (value: XCSafetyScenario[]) => {
      setSafetyScenarioList(value)
      localStorage.setItem(
        'ai_recognition_safety_scenario_list',
        JSON.stringify(value)
      )
    },
    []
  )

  // 参考标准操作
  const updateReferenceStandard = useCallback(
    (value: string[]) => {
      if (!recognitionResult) return

      setRecognitionResult({
        ...recognitionResult,
        standardList: value
      })
    },
    [recognitionResult, setRecognitionResult]
  )

  // 思考过程操作
  const updateThinkingProcess = useCallback(
    (value: string) => {
      if (!recognitionResult) return

      setRecognitionResult({
        ...recognitionResult,
        thinking: value
      })
    },
    [recognitionResult, setRecognitionResult]
  )

  // 开始识别操作
  const startRecognition = useCallback(() => {
    if (selectedImages.length === 0) {
      console.warn('没有选择图片，无法开始识别')
      return
    }

    setRecognitionStatus(XC_RECOGNITION_STATUS.RECOGNIZING)
  }, [selectedImages, setRecognitionStatus])

  // 批量模式更新方法
  const setIsBatchMode = useCallback((isBatch: boolean) => {
    setIsBatchModeState(isBatch)
  }, [])

  const setBatchProgress = useCallback((progress: XCBatchProgress) => {
    setBatchProgressState(progress)
  }, [])

  // 筛选参数状态
  const [filterParams, setFilterParamsState] = useState<XCFilterParams>({
    keywords: '',
    questionTypeList: [],
    monomerName: '',
    departmentUsers: [],
    departments: [],
    startTime: undefined,
    endTime: undefined
  })

  // 包装 setFilterParams 为简单函数类型
  const setFilterParams = useCallback((params: XCFilterParams) => {
    setFilterParamsState((prevParams) => ({
      ...prevParams,
      ...params
    }))
  }, [])

  // 重置筛选参数
  const resetFilterParams = useCallback(() => {
    setFilterParamsState({
      keywords: '',
      questionTypeList: [],
      monomerName: '',
      departmentUsers: [],
      departments: [],
      startTime: undefined,
      endTime: undefined
    })
  }, [])

  // Context值
  const contextValue: XCAISecurityContext = {
    // 状态
    selectedImages,
    recognitionStatus,
    recognitionResult,
    selectionInfo,
    selectedSafetyScenarios,
    safetyScenarioList,
    userId,
    groupId,
    isGroupVIP: isVip,
    myRole,
    imageSelectionEditorRef,
    rememberChoice,
    showSafetyScenarioModal,
    filterParams,
    isBatchMode,
    batchProgress,
    // 基础操作
    setSelectedImages,
    setRememberChoice: setRememberChoiceWithStorage,
    setRecognitionResult,
    setRecognitionStatus,
    setSelectionInfo,
    setSelectedSafetyScenarios: setSelectedSafetyScenariosWithStorage,
    setSafetyScenarioList: setSafetyScenarioListWithStorage,
    setImageSelectionEditorRef,
    setFilterParams,
    resetFilterParams,

    // 识别操作
    startRecognition,

    // 参考标准操作
    updateReferenceStandard,

    // 思考过程操作
    updateThinkingProcess,

    // 安全场景Modal操作
    setShowSafetyScenarioModal,

    // 批量模式
    setIsBatchMode,
    setBatchProgress
  }

  return (
    <AISecurityContext.Provider value={contextValue}>
      {children}
    </AISecurityContext.Provider>
  )
}

// Hook for using context
export const useAISecurity = (): XCAISecurityContext => {
  const context = useContext(AISecurityContext)
  if (context === undefined) {
    throw new Error('useAISecurity must be used within an AISecurityProvider')
  }
  return context
}

export default AISecurityContext
